import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, fmtPrice } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { toast } from "sonner";
import { ShoppingCart, Minus, Plus, Check, Truck, ShieldCheck, ChevronRight, ImageOff, PlayCircle, Heart, Star } from "lucide-react";

export default function ProductDetail() {
  const { sku } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [imgErr, setImgErr] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [myRating, setMyRating] = useState(5);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const { isFav, toggle } = useFavorites();
  const navigate = useNavigate();

  const loadReviews = useCallback(() => {
    axios.get(`${API}/products/${sku}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [sku]);

  useEffect(() => {
    setLoading(true);
    setImgErr(false);
    setQty(1);
    axios.get(`${API}/products/${sku}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    loadReviews();
    window.scrollTo({ top: 0 });
  }, [sku, loadReviews]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Connectez-vous pour laisser un avis"); navigate("/compte"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products/${sku}/reviews`, { rating: myRating, comment: myComment });
      setMyComment("");
      toast.success("Merci pour votre avis !");
      loadReviews();
    } catch { toast.error("Impossible d'enregistrer votre avis"); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 py-10 md:grid-cols-2">
        <Skeleton className="h-96 w-full" />
        <div><Skeleton className="h-8 w-3/4" /><Skeleton className="mt-4 h-10 w-1/3" /></div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-24 text-center">
        <p className="text-lg text-slate-500">Produit introuvable.</p>
        <Button className="mt-4" onClick={() => navigate("/catalogue")}>Retour au catalogue</Button>
      </div>
    );
  }

  const p = data.product;
  const out = !p.inStock;
  const videoId = p.video ? (p.video.split("v=")[1] || "").split("&")[0] : "";

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-10">
      {/* breadcrumb */}
      <nav className="mb-6 flex flex-wrap items-center gap-1 text-sm text-slate-500">
        <Link to="/" className="hover:text-blue-700">Accueil</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/catalogue?department=${encodeURIComponent(p.department)}`} className="hover:text-blue-700">{p.department}</Link>
        <ChevronRight className="h-4 w-4" />
        <span className="truncate text-slate-700">{p.category}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {/* image */}
        <div className="relative flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-8">
          {p.discount > 0 && (
            <span className="absolute left-4 top-4 rounded-full bg-blue-600 px-3 py-1 text-sm font-bold text-white">-{p.discount}%</span>
          )}
          {out && (
            <span className="absolute right-4 top-4 rounded-full bg-red-600 px-3 py-1 text-sm font-bold text-white" data-testid="detail-out-of-stock">Rupture de stock</span>
          )}
          {p.image && !imgErr ? (
            <img src={p.image} alt={p.name} onError={() => setImgErr(true)} className={`max-h-[420px] w-full object-contain ${out ? "opacity-60 grayscale" : ""}`} />
          ) : (
            <div className="flex flex-col items-center gap-2 py-24 text-slate-300"><ImageOff className="h-12 w-12" /><span className="text-sm">Image indisponible</span></div>
          )}
        </div>

        {/* info */}
        <div>
          <span className="text-sm font-semibold uppercase tracking-wide text-blue-600">{p.category}</span>
          <h1 className="mt-2 font-head text-2xl font-extrabold text-slate-900 md:text-3xl" data-testid="detail-name">{p.name}</h1>
          <div className="mt-2 flex items-center gap-3">
            <p className="text-sm text-slate-400">Réf. {p.sku}</p>
            {reviews.count > 0 && (
              <span className="flex items-center gap-1 text-sm text-slate-500" data-testid="detail-rating">
                <StarRating value={reviews.average} size={15} />
                <span className="font-semibold text-slate-700">{reviews.average}</span>
                <span className="text-slate-400">({reviews.count})</span>
              </span>
            )}
          </div>

          <div className="mt-6 flex items-end gap-3">
            <span className="font-head text-4xl font-extrabold text-slate-900" data-testid="detail-price">{fmtPrice(p.price)}</span>
            {p.oldPrice && <span className="pb-1 text-lg text-slate-400 line-through">{fmtPrice(p.oldPrice)}</span>}
          </div>
          <p className="mt-1 text-xs text-slate-400">Prix TTC</p>

          <div className="mt-5">
            {out ? (
              <span className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-600">
                Produit en rupture de stock
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm font-semibold text-green-700">
                <Check className="h-4 w-4" /> En stock — expédié sous 24/48h
              </span>
            )}
          </div>

          <div className="mt-4">
            <Button
              variant="outline"
              data-testid="detail-fav-toggle"
              onClick={() => toggle(p)}
              className="rounded-full border-slate-300"
            >
              <Heart className={`mr-2 h-4 w-4 ${isFav(p.sku) ? "fill-red-500 text-red-500" : "text-slate-600"}`} />
              {isFav(p.sku) ? "Dans vos favoris" : "Ajouter aux favoris"}
            </Button>
          </div>

          {!out && (
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-full border border-slate-200">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-blue-700" data-testid="detail-qty-minus"><Minus className="h-4 w-4" /></button>
                <span className="w-10 text-center font-semibold" data-testid="detail-qty">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 hover:text-blue-700" data-testid="detail-qty-plus"><Plus className="h-4 w-4" /></button>
              </div>
              <Button
                data-testid="detail-add-to-cart"
                onClick={() => add(p, qty)}
                className="flex-1 rounded-full bg-blue-600 py-6 text-base text-white hover:bg-blue-700 min-w-[200px]"
              >
                <ShoppingCart className="mr-2 h-5 w-5" /> Ajouter au panier
              </Button>
            </div>
          )}

          <div className="mt-8 space-y-3 border-t border-slate-100 pt-6 text-sm">
            <div className="flex items-center gap-3 text-slate-600"><Truck className="h-5 w-5 text-blue-600" /> Livraison en France & Benelux</div>
            <div className="flex items-center gap-3 text-slate-600"><ShieldCheck className="h-5 w-5 text-blue-600" /> Paiement 100% sécurisé via Stripe</div>
          </div>

          {videoId && (
            <a href={p.video} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline">
              <PlayCircle className="h-5 w-5" /> Voir la vidéo du produit
            </a>
          )}
        </div>
      </div>

      {/* AVIS CLIENTS */}
      <section className="mt-16 border-t border-slate-100 pt-10" data-testid="reviews-section">
        <h2 className="font-head text-2xl font-extrabold text-slate-900">Avis clients</h2>
        <div className="mt-2 flex items-center gap-3">
          <StarRating value={reviews.average} size={20} />
          <span className="text-lg font-bold text-slate-900">{reviews.average || "—"}</span>
          <span className="text-sm text-slate-500">{reviews.count} avis</span>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-3">
          {/* form */}
          <div className="lg:col-span-1">
            <h3 className="font-head text-lg font-bold text-slate-900">Donnez votre avis</h3>
            {user ? (
              <form onSubmit={submitReview} className="mt-3 space-y-3" data-testid="review-form">
                <StarRating value={myRating} size={26} onChange={setMyRating} />
                <Textarea
                  value={myComment}
                  onChange={(e) => setMyComment(e.target.value)}
                  placeholder="Partagez votre expérience sur ce produit..."
                  data-testid="review-comment"
                  className="min-h-[100px]"
                />
                <Button type="submit" disabled={submitting} data-testid="review-submit" className="rounded-full bg-blue-600 text-white hover:bg-blue-700">
                  Publier mon avis
                </Button>
              </form>
            ) : (
              <p className="mt-3 text-sm text-slate-500">
                <Link to="/compte" className="font-semibold text-blue-700 hover:underline">Connectez-vous</Link> pour laisser un avis.
              </p>
            )}
          </div>

          {/* list */}
          <div className="lg:col-span-2">
            {reviews.reviews.length === 0 ? (
              <p className="text-sm text-slate-400">Aucun avis pour le moment. Soyez le premier !</p>
            ) : (
              <div className="space-y-5" data-testid="reviews-list">
                {reviews.reviews.map((r) => (
                  <div key={r.review_id} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-800">{r.user_name}</span>
                      <StarRating value={r.rating} size={15} />
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
                    <p className="mt-2 text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {data.related?.length > 0 && (
        <section className="mt-16">
          <h2 className="mb-6 font-head text-2xl font-extrabold text-slate-900">Vous aimerez aussi</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {data.related.map((r) => <ProductCard key={r.sku} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
